import "@frontend/styles/components/modal.css";
import ajax from "@common/scripts/ajax";
import { on, fire } from "delegated-events";
import A11yDialog from "a11y-dialog";

const generateTemplate = (id, type, title = false, hideClose = false) => {
  const role = hideClose ? "alertdialog" : "dialog";
  const labeledBy = title ? 'aria-labelledby="modal-title"' : "";

  let showClose = hideClose === false;

  if (type === "image") {
    showClose = false;
  }

  const html = `
    <div id="${id}" aria-hidden="true" class="modal-wrapper" ${labeledBy} role="${role}">
      <div class="modal-overlay" data-a11y-dialog-hide></div>

      ${
        showClose
          ? '<button type="button" class="modal-close" data-a11y-dialog-hide aria-label="Close dialog"> <svg viewBox="0 0 24 24" aria-hidden="true"> <path fill="currentColor" d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" /> </svg> </button>'
          : ""
      }

      <div class="modal-container">
        <svg class="site-loader site-loader--centered site-loader--light" viewBox="0 0 50 50" aria-hidden="true">
          <circle class="path" cx="25" cy="25" r="20" fill="none" stroke-width="5"></circle>
        </svg>
      </div>
    </div>
  `;

  return html;
};

const populateModal = async (opts = {}, modalEl) => {
  const type = opts.type;
  const title = opts.title ?? "";
  const url = opts.url;

  let modalContent = "";

  if (type === "video") {
    modalContent = `
      <div class="mx-auto w-full max-w-5xl modal-content" role="document">
        <h1 id="modal-title" class="sr-only">Watch ${title}</h1>
        <div class="relative aspect-video modal-body">
          <iframe title="Watch ${title}" style="border: none" src="${url}?autoplay=1&rel=0&enablejsapi=1" allowfullscreen class="absolute inset-0 w-full h-full"></iframe>
        </div>
      </div>
    `;

    modalEl.querySelector(".modal-container").innerHTML = modalContent;
  }

  if (type === "image") {
    modalContent = `
      <div class="relative mx-auto w-full max-w-2xl modal-content" role="document" autofocus>
        <h1 id="modal-title" class="sr-only">${title}</h1>
        <div class="relative modal-body">
          <button type="button" class="block mb-2 ml-auto text-white transition-opacity hover:opacity-40 size-10" data-a11y-dialog-hide aria-label="Close dialog">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path fill="currentColor" d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
            </svg>
          </button>
          <img src="${url}" alt="${title}" class="w-full" />
        </div>
      </div>
    `;

    modalEl.querySelector(".modal-container").innerHTML = modalContent;
  }

  if (type === "content") {
    modalContent = await ajax.get(url).text();

    modalEl.querySelector(".modal-container").innerHTML = modalContent;
  }

  fire(document.documentElement, "modal:populate", { modalEl });
};

const handleModal = (e) => {
  e.preventDefault();

  const triggerEl = e.target.closest("[data-modal]");

  openModal({
    type: triggerEl.dataset.modal,
    title: triggerEl.dataset.modalTitle,
    url: triggerEl.href,
    hideClose: triggerEl.dataset.modalHideClose ?? false,
  });

  return false;
};

const openModal = (opts = {}) => {
  const type = opts.type;
  const title = opts.title ?? false;
  const hideClose = opts.hideClose;

  const id = `modal-${Math.floor(Math.random() * Date.now())}`;
  const template = generateTemplate(id, type, title, hideClose);
  const node = document.createRange().createContextualFragment(template);

  document.body.append(node);

  const modalEl = document.getElementById(id);
  const modal = new A11yDialog(modalEl);

  modal.on("show", () => {
    document.documentElement.classList.add("has-modal-active");
  });

  modal.on("hide", () => {
    document.documentElement.classList.remove("has-modal-active");

    modal.destroy();
  });

  modal.on("destroy", (e) => {
    document.querySelector(".modal-wrapper").remove();
    e.target.remove();
  });

  modal.show();

  populateModal(
    {
      type: type,
      url: opts.url ?? false,
    },
    modalEl,
  );
};

export default {
  init: () => {
    on("click", "[data-modal]", handleModal);

    // If we have video modals, load the YouTube API
    if (document.querySelectorAll('[data-modal="video"]').length) {
      const tag = document.createElement("script");
      tag.src = "https://www.youtube.com/iframe_api";

      const firstScriptTag = document.getElementsByTagName("script")[0];

      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
    }
  },
  openModal,
};
